import { useCallback } from 'react';
import { useOrganization } from '@clerk/react';
import {
  pickDocuments,
  pickFolder,
  pickSpreadsheet,
  type PickedFile,
} from '@/lib/google-picker';
import {
  useGetPickerTokenMutation,
  useGetWorkspaceStatusQuery,
} from '@/store/features/workspace-connections.feature';
import { apiErrorCode } from '@/lib/appsumo-upgrade';

/**
 * Whether the signed-in member can pick from their own connected Google, and
 * the pick itself (CLE-515): a Sheet with Google Sheets connected (`pick`),
 * Google Docs with Google Docs connected (`pickDocs`, for the knowledge base
 * and a play's Google Docs trigger), or a Drive folder (`pickFolder`, for
 * Create a Google Doc).
 */
export function useGooglePicker(
  provider: 'google_sheets' | 'google_docs' = 'google_sheets'
) {
  const { organization } = useOrganization();
  const organizationId = organization?.id ?? '';
  const { currentData: status } = useGetWorkspaceStatusQuery(
    { provider, organizationId },
    { skip: !organizationId }
  );
  const [getPickerToken] = useGetPickerTokenMutation();
  const connected = !!status?.connected;

  const credentials = useCallback(
    () => getPickerToken({ provider, organizationId }).unwrap(),
    [getPickerToken, provider, organizationId]
  );
  const pick = useCallback(
    async (fileId?: string): Promise<PickedFile | null> =>
      pickSpreadsheet({ ...(await credentials()), fileId }),
    [credentials]
  );
  const pickDocs = useCallback(
    async (fileIds?: string[]): Promise<PickedFile[]> =>
      pickDocuments({ ...(await credentials()), fileIds }),
    [credentials]
  );
  const pickDriveFolder = useCallback(
    async (fileId?: string): Promise<PickedFile | null> =>
      pickFolder({ ...(await credentials()), fileId }),
    [credentials]
  );

  return {
    connected,
    canPick: connected,
    accountLabel: status?.accountLabel ?? null,
    organizationId,
    pick,
    pickDocs,
    pickFolder: pickDriveFolder,
  };
}

/** A toast-ready sentence for a failed pick or read. */
export function googlePickError(err: unknown): string {
  switch (apiErrorCode(err)) {
    case 'picker_not_configured':
      return 'Picking from Google isn’t set up yet — check back soon.';
    case 'sheet_unreachable':
      return 'Google wouldn’t share that sheet. Try picking it again.';
    case 'not_connected':
      return 'Connect your Google account on the Integrations page first.';
    default:
      return 'Couldn’t read the sheet from Google. Try again.';
  }
}
